import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider,
  useTheme,
  Button,
  Snackbar,
  AlertTitle
} from '@mui/material';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import AirIcon from '@mui/icons-material/Air';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import AgricultureIcon from '@mui/icons-material/Agriculture';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import { weatherService } from '../../../services/weatherService';

interface WeatherData {
  temperature: number;
  humidity: number;
  windSpeed: number;
  description: string;
  icon: string;
}

interface AgriculturalUpdate {
  id: string;
  title: string;
  description: string;
  timestamp: string;
  type: 'alert' | 'info' | 'warning';
}

interface WeatherProps {
  cityName?: string;
  farmLocation?: string;
}

const Weather: React.FC<WeatherProps> = ({ cityName, farmLocation }) => {
  const [weatherData, setWeatherData] = useState<WeatherData | null>(null);
  const [agriculturalUpdates, setAgriculturalUpdates] = useState<AgriculturalUpdate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [location, setLocation] = useState<{lat: number, lon: number}>({ 
    // Default to Nairobi coordinates
    lat: -1.2921, 
    lon: 36.8219 
  });
  const [locationName, setLocationName] = useState<string>(cityName || farmLocation || 'Nairobi, Kenya');
  const [usingUserLocation, setUsingUserLocation] = useState<boolean>(false);
  const [locationLoading, setLocationLoading] = useState<boolean>(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const theme = useTheme();

  // Get user location
  const getUserLocation = () => {
    setLoading(true);
    setLocationLoading(true);
    setStatusMessage("Detecting your location...");
    
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setLocation({ lat: latitude, lon: longitude });
          setUsingUserLocation(true);
          setStatusMessage("Location detected! Fetching weather data...");
          fetchWeatherData(latitude, longitude);
          // Attempt to get location name
          fetch(`https://api.openweathermap.org/geo/1.0/reverse?lat=${latitude}&lon=${longitude}&limit=1&appid=${import.meta.env.VITE_WEATHER_API_KEY}`)
            .then(res => res.json())
            .then(data => {
              if (data && data[0]) {
                setLocationName(`${data[0].name}, ${data[0].country}`);
                setStatusMessage(null);
                setLocationLoading(false);
              }
            })
            .catch(() => {
              // Keep the default if reverse geocoding fails
              setStatusMessage(null);
              setLocationLoading(false);
            });
        },
        (error) => {
          console.error('Error getting location:', error);
          setError('Could not get your location. Using default location.');
          setStatusMessage("Using default location instead");
          // Use default location
          fetchWeatherData(location.lat, location.lon);
          setLocationLoading(false);
        }
      );
    } else {
      setError('Geolocation is not supported by your browser. Using default location.');
      setStatusMessage("Using default location instead");
      fetchWeatherData(location.lat, location.lon);
      setLocationLoading(false);
    }
  };

  // Helper: Geocode farm location to coordinates
  const geocodeFarmLocation = async (address: string) => {
    setStatusMessage(`Locating farm: ${address}...`);
    setLocationLoading(true);
    try {
      const geoRes = await fetch(
        `https://api.openweathermap.org/geo/1.0/direct?q=${encodeURIComponent(address)}&limit=1&appid=${import.meta.env.VITE_WEATHER_API_KEY}`
      );
      const geoData = await geoRes.json();
      if (geoData && geoData.length > 0) {
        const { lat, lon, name, country, state } = geoData[0];
        setLocation({ lat, lon });
        setLocationName(`${name}${state ? ', ' + state : ''}, ${country}`);
        setStatusMessage(null);
        setLocationLoading(false);
        fetchWeatherData(lat, lon);
        return true;
      } else {
        setError('Could not find farm location. Using default location.');
        setStatusMessage('Using default location instead');
        setLocationLoading(false);
        fetchWeatherData(location.lat, location.lon);
        return false;
      }
    } catch (err) {
      setError('Failed to geocode farm location. Using default location.');
      setStatusMessage('Using default location instead');
      setLocationLoading(false);
      fetchWeatherData(location.lat, location.lon);
      return false;
    }
  };

  // Fetch weather data
  const fetchWeatherData = async (lat: number, lon: number) => {
    try {
      setStatusMessage("Fetching weather data...");
      const data = await weatherService.getWeatherByCoordinates(lat, lon);
      setWeatherData(data);
      setError(null);
      setStatusMessage(null);
    } catch (err) {
      setError('Failed to fetch weather data');
      setStatusMessage(null);
      console.error('Weather fetch error:', err);
    }
  };

  // Fetch agricultural updates
  const fetchAgriculturalUpdates = async () => {
    try {
      const [updates, alerts] = await Promise.all([
        weatherService.getAgriculturalUpdates(),
        weatherService.getAgriculturalAlerts()
      ]);
      
      // Combine and sort updates and alerts by timestamp
      const combinedUpdates = [...updates, ...alerts]
        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
      
      setAgriculturalUpdates(combinedUpdates);
    } catch (err) {
      setError('Failed to fetch agricultural updates');
      console.error('Agricultural updates fetch error:', err);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      // Priority: farmLocation > cityName > user location > default
      if (farmLocation) {
        await geocodeFarmLocation(farmLocation);
      } else if (cityName) {
        setStatusMessage(`Fetching weather for ${cityName}...`);
        try {
          const data = await weatherService.getWeatherByCity(cityName);
          setWeatherData(data);
          setLocationName(cityName);
          setError(null);
          setStatusMessage(null);
        } catch (err) {
          setError(`Failed to fetch weather data for ${cityName}`);
          setStatusMessage(null);
          console.error('Weather fetch error:', err);
        }
      } else {
        // Try to get user's location, will fallback to default if not available
        getUserLocation();
      }
      await fetchAgriculturalUpdates();
      setLoading(false);
    };

    fetchData();

    // Set up polling for real-time updates
    const weatherInterval = setInterval(() => {
      if (cityName) {
        weatherService.getWeatherByCity(cityName).then(setWeatherData);
      } else {
        fetchWeatherData(location.lat, location.lon);
      }
    }, 300000); // Every 5 minutes
    
    const updatesInterval = setInterval(fetchAgriculturalUpdates, 600000); // Every 10 minutes

    return () => {
      clearInterval(weatherInterval);
      clearInterval(updatesInterval);
    };
  }, [cityName, farmLocation]);

  if (loading) {
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress sx={{ mb: 2 }} />
        {statusMessage && (
          <Typography variant="body1" color="textSecondary" textAlign="center">
            {statusMessage}
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ py: 2 }}>
      {/* Status message snackbar */}
      <Snackbar 
        open={!!statusMessage && !loading} 
        autoHideDuration={6000} 
        onClose={() => setStatusMessage(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="info" onClose={() => setStatusMessage(null)}>
          {statusMessage}
        </Alert>
      </Snackbar>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">
          Weather for {locationName}
        </Typography>
        <Button 
          startIcon={<MyLocationIcon />} 
          variant="outlined"
          onClick={getUserLocation}
          disabled={locationLoading}
          size="small"
        >
          {locationLoading ? 'Detecting...' : 'Use My Location'}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Weather Card */}
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WbSunnyIcon /> Current Weather
              </Typography>
              {weatherData && (
                <Grid container spacing={2} sx={{ mt: 2 }}>
                  <Grid item xs={6}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <ThermostatIcon color="primary" />
                      <Typography variant="h4">{weatherData.temperature}°C</Typography>
                      <Typography variant="body2" color="textSecondary">Temperature</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <WaterDropIcon color="primary" />
                      <Typography variant="h4">{weatherData.humidity}%</Typography>
                      <Typography variant="body2" color="textSecondary">Humidity</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <AirIcon color="primary" />
                      <Typography variant="h4">{weatherData.windSpeed} m/s</Typography>
                      <Typography variant="body2" color="textSecondary">Wind Speed</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <img 
                        src={`https://openweathermap.org/img/w/${weatherData.icon}.png`}
                        alt={weatherData.description}
                        style={{ width: 50, height: 50 }}
                      />
                      <Typography variant="body2" color="textSecondary">
                        {weatherData.description}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Agricultural Updates Card */}
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AgricultureIcon /> Agricultural Updates
              </Typography>
              <List>
                {agriculturalUpdates.map((update, index) => (
                  <React.Fragment key={update.id}>
                    <ListItem key={`item-${update.id}`} alignItems="flex-start">
                      <ListItemText
                        primary={
                          <Typography variant="subtitle1" color="primary">
                            {update.title}
                          </Typography>
                        }
                        secondary={
                          <Box component="span">
                            <Typography 
                              component="span" 
                              variant="body2" 
                              color="textSecondary"
                              display="block"
                            >
                              {update.description}
                            </Typography>
                            <Typography 
                              component="span" 
                              variant="caption" 
                              color="textSecondary"
                              display="block"
                            >
                              {new Date(update.timestamp).toLocaleString()}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < agriculturalUpdates.length - 1 && <Divider key={`divider-${update.id}`} />}
                  </React.Fragment>
                ))}
                {agriculturalUpdates.length === 0 && (
                  <ListItem>
                    <ListItemText primary="No updates available" />
                  </ListItem>
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Weather; 